import {ready, subscribe, unsubscribe} from '@payloadcms/live-preview';
import {action, makeAutoObservable} from 'mobx';

import {isMergeable, isObject} from 'Application/utils/helpers';

/**
 * A service class that manages real-time live preview subscriptions, state updates, and data synchronization.
 * `LivePreviewService` enables seamless integration with a live preview server by managing incoming updates via
 * subscriptions, merging new data into a reactive MobX state tree. It supports deep merging of objects and arrays,
 * selective data retrieval, and lifecycle management for preview subscriptions. Designed to work in environments where
 * structured preview data is pushed from a server (e.g., CMS-driven content previews).
 */
export class LivePreviewService {
    /**
     * The URL of the live preview server to which this service connects.
     * This is set when the service is initialized or when a new subscription is created.
     */
    serverURL: string | undefined;

    /**
     * Stores preview data for each subscription, keyed by `storeKey`.
     */
    previewData: Record<string, unknown> = {};

    /**
     * Stores active subscription callbacks, keyed by `storeKey`.
     */
    subscriptions: Record<string, ((event: MessageEvent) => Promise<void> | void)> = {};

    /**
     * Constructs a new instance of the `LivePreviewService` and enables MobX auto-observability.
     * MobX's `makeAutoObservable` is used to automatically make fields and methods observable and reactive.
     * This allows UI components to respond automatically to state changes within this service.
     * The constructor also binds all methods to the class instance for consistent behavior.
     */
    constructor() {
        makeAutoObservable(this, {}, {autoBind: true});
    }

    /**
     * Subscribes to live preview updates and merges incoming data into the local state store.
     * This method sets up a subscription for a given `storeKey`, only if it hasn't been initialized already.
     * When new data arrives from the preview server, it is deeply merged into the existing preview data structure.
     * The method ensures that only relevant updates are applied by checking the `livePreviewName` field in incoming data.
     *
     * @param initialData The initial preview data to store for the subscription.
     * @param depth       The depth of the nested structure to be fetched and observed.
     * @param storeKey    A unique identifier for the subscription and corresponding preview data.
     * @param serverURL   The URL of the preview server to connect to.
     */
    subcribe<T extends Record<string, any>>(initialData: T, depth: number, storeKey: string, serverURL: string) {
        if (this.serverURL !== serverURL) {
            this.serverURL = serverURL;
        }
        if (typeof this.previewData[storeKey] === 'undefined') {
            this.previewData[storeKey] = initialData;
        }
        if (typeof this.subscriptions[storeKey] === 'undefined') {
            this.subscriptions[storeKey] = subscribe<T>({
                callback: (data: T) => {
                    if (data.livePreviewName === storeKey) {
                        action(() => {
                            this.deepMerge(this.previewData[storeKey], data);
                        })();
                    }
                },
                depth,
                initialData,
                serverURL
            });
        }
    }

    /**
     * Unsubscribes from a live preview stream associated with a specific `storeKey`.
     * This method removes the subscription callback from the internal registry and calls the `unsubscribe` utility.
     * It is a cleanup operation that ensures no further updates are received from the preview server for the given key.
     * After calling this method, the associated preview data remains in memory but will no longer be updated.
     *
     * @param storeKey The key of the subscription to cancel.
     */
    unsubscribe(storeKey: string) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (this.subscriptions[storeKey]) {
            unsubscribe(this.subscriptions[storeKey]);
            delete this.subscriptions[storeKey];
            delete this.previewData[storeKey];
        }
    }

    /**
     * Notifies the live preview server that the frontend is ready to receive real-time updates.
     * This should be called once the application or page is fully mounted and ready to respond to preview data.
     * The server may delay sending data until this signal is received, depending on its implementation.
     * The method is typically invoked from a wrapper component that signals hydration readiness.
     */
    ready() {
        ready({serverURL: this.serverURL ?? ''});
    }

    /**
     * Retrieves preview data stored for a given `storeKey`, cast to the expected type.
     * This is a typed accessor that returns the current state of the previewed document or entity.
     * It enables components to access up-to-date content, which may have been merged in through live updates.
     * If no preview data exists for the key, `undefined` is returned.
     *
     * @param storeKey The key associated with the preview data to retrieve.
     * @returns The preview data object of type `T`, or `undefined` if not present.
     */
    getPreviewData<T extends Record<string, any>>(storeKey: string): T | undefined {
        return this.previewData[storeKey] as T | undefined;
    }

    /**
     * Deeply merges two values — either objects or arrays — into the target object.
     * This method modifies the target in place and handles complex nested structures, preserving existing data
     * wherever possible. Arrays are merged by index, and objects are merged key by key recursively.
     * Non-mergeable values in the source will overwrite the corresponding value in the target.
     *
     * @param target The destination object or array to be updated.
     * @param source The source object or array providing the new data.
     * @returns The modified target object or array after merging.
     */
    deepMerge(target: any, source: any) {
        if (Array.isArray(target) && Array.isArray(source)) {
            const {length} = source;

            for (let i = 0; i < length; i++) {
                if (i in source) {
                    if (i in target) {
                        if (isMergeable(target[i]) && isMergeable(source[i])) {
                            this.deepMerge(target[i], source[i]);
                        } else {
                            // eslint-disable-next-line no-param-reassign
                            target[i] = source[i];
                        }
                    } else {
                        // eslint-disable-next-line no-param-reassign
                        target[i] = source[i];
                    }
                }
            }
            // eslint-disable-next-line no-param-reassign
            target.length = length;

            return target;
        }

        if (isObject(target) && isObject(source)) {
            for (const key in source) {
                if (Object.hasOwnProperty.call(source, key)) {
                    if (key in target) {
                        // eslint-disable-next-line no-param-reassign
                        target[key] = this.deepMerge(target[key], source[key]);
                    } else {
                        // eslint-disable-next-line no-param-reassign
                        target[key] = source[key];
                    }
                }
            }

            return target;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return source;
    }
}

export const LivePreviewServiceInstance = new LivePreviewService();